import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { EAccountRole, ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { FiliaisService } from '../../services/filiais.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AddressViaCEP } from 'src/app/shared/data/address-via-cep';
import { ICadastroFilial } from '../../data/filial';
import { CompanyService } from 'src/app/pages/companies/services/company.service';
import { ICompany } from 'src/app/pages/companies/models/company.interface';
import { canPickCompany } from 'src/app/shared/utilities/company-scope';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';

@Component({
  selector: 'app-filiais-edit',
  templateUrl: './filiais-edit.component.html',
  styleUrls: ['./filiais-edit.component.scss']
})
export class FiliaisEditComponent implements OnInit {
  ERoleAdminString = ERoleAdminString;

  @Output() recarregarListagem = new EventEmitter();
  @Output() cancelarEdit = new EventEmitter();
  @Output() finalizarEdit = new EventEmitter();

  @Input() filialId?: string | null;
  opcoesListCompany: ICompany[] = [];
  podeEscolherEmpresa = false;

  formEditFilial = new UntypedFormGroup({
    id: new UntypedFormControl('', [Validators.required]),
    name: new UntypedFormControl('', [Validators.required]),
    code: new UntypedFormControl('', [Validators.required]),
    // CNPJ é opcional (OE-1767); a máscara entrega só dígitos, então incompleto é menos de 14.
    document: new UntypedFormControl('', [Validators.minLength(14)]),
    zipCode: new UntypedFormControl('', [Validators.required]),
    address: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
    number: new UntypedFormControl('', [Validators.required]),
    complement: new UntypedFormControl(''),
    city: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
    district: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
    state: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
    street: new UntypedFormControl(null),
    companyId: new UntypedFormControl(null, [Validators.required]),
    companyName: new UntypedFormControl('')
  });

  companyId?: string;
  partnerId?: string;
  accountRole?: EAccountRole;

  private readonly cepInput: Subject<string> = new Subject<string>();
  public cepError = false;

  constructor(
    private readonly toastService: ToastService,
    private readonly companyService: CompanyService,
    private readonly filialisservice: FiliaisService,
    private readonly sessionService: SessionService
  ) {
    // Evita chamadas a mais se fosse ficar procurando CEP toda vez que alterar o input
    this.cepInput.pipe(debounceTime(500)).subscribe((cep) => {
      if (cep.length === 9) {
        this.getCEP(cep, this.formEditFilial);
      }
    });
  }

  // A filial é carregada no ngOnChanges, que roda antes do ngOnInit com o filialId inicial.
  ngOnInit(): void {
    this.partnerId = this.sessionService.getSession().partnerId;
    this.companyId = this.sessionService.getSession().companyId;
    this.accountRole = parseInt(this.sessionService.getSession().accountRole);

    if (this.companyId) {
      this.formEditFilial.get('companyId')?.disable();
    }
  }

  public carregarDetalhesFiliais(filialId: string): void {
    this.formEditFilial.get('id')!.patchValue(filialId);

    this.filialisservice.detalhesFilial(filialId).subscribe((res) => {
      if (res.messages && res.messages.length > 0) {
        this.toastService.toast(res.messages);
      }
      if (res.isSucceed) {
        this.popularCamposFormulario(res.data);
        // CNPJ informado não muda; filial sem CNPJ pode receber um na edição.
        if (res.data.document) {
          this.formEditFilial.get('document')?.disable();
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filialId']) {
      this.carregaDadosFilial();
    }
  }

  editFilial(): void {
    if (this.formEditFilial.invalid) {
      this.toastService.toast('Preencha todos os campos obrigatórios antes de salvar.', EMessageType.Error);
      this.formEditFilial.markAllAsTouched();
      return;
    }

    // Endereço vem do CEP e os campos ficam desabilitados (fora da validação do form).
    if (this.cepError || !this.formEditFilial.getRawValue().city) {
      this.toastService.toast('Informe um CEP válido para preencher o endereço.', EMessageType.Error);
      return;
    }

    const dadosFormulario = this.formEditFilial.getRawValue();
    const empresaVinculada = this.opcoesListCompany.find((company) => company.id === dadosFormulario.companyId);
    // Usuário de empresa não carrega a lista: mantém o nome que veio da filial.
    this.formEditFilial.get('companyName')?.patchValue(empresaVinculada?.name ?? dadosFormulario.companyName);

    this.filialisservice.editarFilial(this.formEditFilial.getRawValue()).subscribe({
      next: (res) => {
        if (res.messages && res.messages.length > 0) {
          this.toastService.toast(res.messages);
        }
        if (res.isSucceed) {
          this.formEditFilial.reset();
          this.recarregarListagem.emit();
          this.finalizarEdit.emit();
        }
      },
      error: () => this.toastService.toast('Erro ao salvar a filial. Tente novamente.', EMessageType.Error)
    });
  }

  getListEmpresas(): void {
    // O backend escopa pelo token: interno vê todas, parceiro só as dele.
    this.companyService.listarEmpresasAtivas().subscribe({
      next: (resposta) => {
        this.opcoesListCompany = resposta.data;
      },
      error: (erro) => {
        this.opcoesListCompany = [];
        if (!interceptorAvisouErro(erro)) {
          this.toastService.toast('Erro ao carregar as empresas. Tente novamente.', EMessageType.Error);
        }
      }
    });
  }

  // Chamado quando há alteração no input
  onCEPEntered(event: Event): void {
    const cep = (event.target as HTMLInputElement).value;
    this.cepInput.next(cep);
  }

  private getCEP(cep: string, f: UntypedFormGroup): void {
    this.cepError = false;
    this.filialisservice.pesquisarCEP(cep).subscribe((resultadoAPI) => {
      if (!resultadoAPI.erro) {
        this.popularEndereco(resultadoAPI, f);
        this.formEditFilial.get('address')?.disable(); // impedir a edição do endereço
      } else {
        this.cepError = true;
      }
    });
  }

  // Coloca os valores retornados da API nos campos corretod
  private popularEndereco(dados: AddressViaCEP, f: UntypedFormGroup): void {
    f.patchValue({
      address: dados.logradouro,
      city: dados.localidade,
      district: dados.bairro,
      state: dados.uf,
      street: dados.logradouro
    });
  }

  onCancelar(): void {
    this.cancelarEdit.emit();
  }

  private popularCamposFormulario(res: ICadastroFilial): void {
    this.formEditFilial.patchValue({
      name: res.name,
      code: res.code,
      document: res.document,
      companyId: res.companyId,
      companyName: res.company?.name,
      zipCode: res.zipCode,
      address: res.street,
      number: res.number,
      complement: res.complement,
      city: res.city,
      district: res.district,
      state: res.state,
      street: res.street
    });
  }

  private carregaDadosFilial(): void {
    if (this.filialId) {
      this.carregarDetalhesFiliais(this.filialId);
    }

    // Usuário de empresa fica fixo na dele (mesmo com parceiro no token); interno e parceiro sem empresa trocam.
    this.podeEscolherEmpresa = canPickCompany(this.sessionService.getSession());
    if (!this.podeEscolherEmpresa) {
      this.formEditFilial.get('companyId')?.disable();
      return;
    }

    this.getListEmpresas();
  }
}
